import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, resolveUrl } from "@/lib/api";
import {
  ArrowRight, ExternalLink, FlaskConical, Wrench, Factory, Leaf,
  Droplets, FileText, MapPin,
} from "lucide-react";

const EMPLOYEE_PORTAL = "https://hma.snawbar.net/dashboard";

const stats = [
  { value: "2009", label: "Established" },
  { value: "2", label: "Production Plants" },
  { value: "9", label: "Regions Covered" },
  { value: "24/7", label: "Technical Support" },
];

const features = [
  { icon: Droplets, title: "Concrete Admixtures", desc: "Polycarboxylate superplasticizers, high-range water reducers, slump retainers, accelerators and retarders." },
  { icon: Factory, title: "Cement Grinding Aids", desc: "Performance enhancers that increase mill throughput and optimise cement strength development." },
  { icon: Wrench, title: "On-Site Technical Support", desc: "Concrete mix optimisation, aggregate analysis and custom formulations for your site conditions." },
  { icon: Leaf, title: "Sustainability", desc: "Eco-conscious products that reduce water and cement consumption without sacrificing performance." },
];

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="bg-slate-50" data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center grain overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1615461476249-718ef8bc369c?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80"
            alt="Concrete construction site"
            className="w-full h-full object-cover [filter:saturate(1.18)_contrast(1.06)]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-50 via-slate-50/55 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-50/70 via-transparent to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest bg-brand-500/10 border border-brand-500/30 text-brand-400">
              <FlaskConical size={13} /> Building Stronger Together
            </span>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900">
              High-Performance <span className="text-brand-400">Concrete Chemicals</span> Engineered for Iraq
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
              Since 2009, HMA Construction Chemicals has engineered high-performance concrete admixtures and cement
              grinding aids — from our plants in Sulaymaniyah and Baghdad to demanding projects across the country.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/products"
                data-testid="hero-view-products"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold transition-colors"
              >
                Browse Data Sheets <ArrowRight size={18} />
              </Link>
              <a
                href={EMPLOYEE_PORTAL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-employee-portal"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-slate-600 hover:border-brand-500/60 text-slate-900 font-semibold transition-colors"
              >
                Employee Portal <ExternalLink size={16} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative border-y border-slate-200 bg-white/40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4 divide-x divide-slate-200">
          {stats.map((s) => (
            <div key={s.label} className="py-10 px-6 text-center" data-testid={`stat-${s.label}`}>
              <div className="font-heading text-4xl font-extrabold text-brand-400">{s.value}</div>
              <div className="mt-2 text-xs uppercase tracking-widest font-mono text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-24 max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-widest font-mono text-brand-400">What we do</span>
          <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">
            Advanced chemical solutions for modern concrete
          </h2>
        </div>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="group p-7 rounded-xl bg-white border border-slate-200 hover:border-brand-500/50 transition-colors"
              data-testid={`feature-${i}`}
            >
              <div className="w-12 h-12 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-400 group-hover:scale-110 transition-transform">
                <f.icon size={22} />
              </div>
              <h3 className="mt-5 font-heading text-lg font-semibold text-slate-900">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PRODUCT PREVIEW */}
      <section className="py-24 bg-white/40 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <span className="text-xs uppercase tracking-widest font-mono text-brand-400">Featured products</span>
              <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">
                Technical data at your fingertips
              </h2>
            </div>
            <Link to="/products" className="inline-flex items-center gap-2 text-brand-400 hover:text-brand-300 font-semibold text-sm" data-testid="home-all-products">
              View all data sheets <ArrowRight size={16} />
            </Link>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {products.map((p) => (
              <Link
                to="/products"
                key={p.id}
                className="group rounded-xl overflow-hidden bg-white border border-slate-200 hover:border-brand-500/50 transition-colors"
                data-testid={`home-product-${p.code}`}
              >
                <div className="h-44 overflow-hidden bg-slate-200">
                  {p.image_url && (
                    <img src={resolveUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-xs font-mono text-brand-400">
                    <FileText size={13} /> {p.code}
                  </div>
                  <h3 className="mt-2 font-heading text-lg font-semibold text-slate-900">{p.name}</h3>
                  <p className="mt-1 text-sm text-slate-500 line-clamp-2">{p.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 max-w-7xl mx-auto px-5 sm:px-8">
        <div className="relative rounded-2xl overflow-hidden border border-slate-200 grain">
          <img
            src="https://images.unsplash.com/photo-1685464196387-854858ce0f4f?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80"
            alt="Concrete pour"
            className="absolute inset-0 w-full h-full object-cover [filter:saturate(1.15)_contrast(1.05)]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-50/92 via-slate-50/65 to-slate-50/25" />
          <div className="relative px-8 py-16 sm:px-16 sm:py-20 flex flex-col items-start">
            <MapPin className="text-brand-400 mb-5" size={36} />
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 max-w-2xl">
              Reliable supply and technical support across Iraq
            </h2>
            <p className="mt-4 text-slate-600 max-w-xl">
              From the Northern Region to the Middle Euphrates, our integrated supply chain delivers prompt service and
              dedicated on-site engineering for every project.
            </p>
            <Link
              to="/contact"
              data-testid="cta-contact"
              className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold transition-colors"
            >
              Talk to our engineers <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
